import { useEffect, useState, type ReactElement } from 'react';
import { useSession } from '../../auth';
import { emberApi } from '../api';
import { chatsUnread } from '../chats';
import { useChatsOnce } from '../chats-store';
import { AccountMenu } from './AccountMenu';
import { Link } from './Link';
import { NotificationBadge } from './NotificationBadge';
import { PresenceAvatar } from './PresenceAvatar';
import { ProfileGlyph } from './ProfileGlyph';
import './NavAccountButton.css';

/**
 * The signed-in row of the landing nav, as one piece so a page that wants the
 * homepage's door can dock the same one — the light's page since 2026-10-01
 * (拍板: that page alone takes the search and the avatar back; the other Back /
 * Home pages stay as they are).
 *
 * The row is the apps' (2026-10-21 founder): Chats and the notebook as doors
 * of their own, each carrying its own count, then the person's animal, which
 * opens the account menu and carries nothing. All three are the search's glass
 * disc.
 */
export function NavAccountButton(): ReactElement {
  const { status } = useSession();
  const [avatar, setAvatar] = useState<string>('');
  useEffect(() => {
    if (status !== 'authenticated') { setAvatar(''); return undefined; }
    let alive = true;
    emberApi.meSummary()
      .then((summary) => { if (alive) setAvatar(summary.avatar_id || ''); })
      .catch(() => { /* the glyph stands in */ });
    return () => { alive = false; };
  }, [status]);
  return (
    <>
      <ChatsDoor />
      <NotebookDoor />
      <AccountMenu buttonClassName="nav-door nav-door-avatar">
        {avatar ? <PresenceAvatar avatarKey={avatar} size={38} /> : <ProfileGlyph />}
      </AccountMenu>
    </>
  );
}

/** Every conversation (Ember's and the rooms'); the count is what is unread across them. */
function ChatsDoor(): ReactElement {
  const { data } = useChatsOnce(true);
  const unread = chatsUnread(data);
  return (
    <Link to="/chats" className="nav-door" aria-label="Chats" data-log="chats_entry" data-log-state="home">
      <ChatsGlyph />
      {unread > 0 ? (
        <span className="nav-door-count" aria-label={`${unread} unread`}>{unread > 99 ? '99+' : unread}</span>
      ) : null}
    </Link>
  );
}

/** The notebook; a dot while a letter or one of Ember's weekly letters waits in it. */
function NotebookDoor(): ReactElement {
  return (
    <Link to="/me/embers" className="nav-door" aria-label="Your notebook" data-log="nav_notebook">
      <NotebookGlyph />
      <NotificationBadge className="nav-door-dot" kind="notebook" />
    </Link>
  );
}

/**
 * The apps' Chats glyph (2026-10-22 Riley), on the apps' 38 canvas: a wide
 * bubble with a tail at its lower left and a smile, a fainter one peeking out
 * top right behind it. The viewBox drops the disc's 1px edge.
 */
function ChatsGlyph(): ReactElement {
  return (
    <svg viewBox="1 1 36 36" width="36" height="36" aria-hidden>
      <rect x="12.25" y="10.99" width="17.1" height="11.7" rx="5.4" fill="currentColor" opacity="0.55" />
      <rect x="8.47" y="13.92" width="18.9" height="13.05" rx="5.4" fill="currentColor" />
      <path d="M11.53 23.05 10.36 30.88 16.03 25.66Z" fill="currentColor" />
      <path d="M12.97 20.08Q17.92 25.48 22.87 20.08" fill="none" stroke="var(--home-nav-btn-smile, #1c1511)" strokeOpacity="0.85" strokeWidth="1.98" strokeLinecap="round" />
    </svg>
  );
}

/** The apps' notebook glyph (2026-10-08 Riley): a cover, four rings through its edge, a label, two rules. */
function NotebookGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" aria-hidden>
      <path strokeWidth="1.6" strokeLinejoin="round" d="M8 2.8h9.5a2 2 0 0 1 2 2v14.4a2 2 0 0 1-2 2H8a1.2 1.2 0 0 1-1.2-1.2V4a1.2 1.2 0 0 1 1.2-1.2z" />
      <path strokeWidth="1.6" strokeLinecap="round" d="M4.4 6.3h4.2M4.4 10.1h4.2M4.4 13.9h4.2M4.4 17.7h4.2" />
      <path strokeWidth="1.4" strokeLinejoin="round" d="M11.4 6.4h5a0.7 0.7 0 0 1 0.7 0.7v2.4a0.7 0.7 0 0 1-0.7 0.7h-5a0.7 0.7 0 0 1-0.7-0.7V7.1a0.7 0.7 0 0 1 0.7-0.7z" />
      <path strokeWidth="1.4" strokeLinecap="round" d="M10.7 14.2h6.4M10.7 17.3h4.4" />
    </svg>
  );
}
